import * as React from "react";
import {
  List, Datagrid, TextField, BooleanField,
  Show, TabbedShowLayout, Edit, Create, SimpleForm,
  TextInput, BooleanInput, required, useRecordContext,
} from "react-admin";
import { JsonField } from "../../components/JsonField";
import { ConfirmActionButton } from "../../components/ConfirmActionButton";
import { RemoteList, fmtDate } from "../../components/RemoteList";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import { FrDateField } from "../../components/FrDateField";
import { UserRefField } from "../../components/ui";
import { ProReferenceInput } from "../../components/RelationAutocomplete";

const pick = (row, ...keys) => {
  for (const k of keys) {
    const parts = k.split(".");
    let v = row;
    for (const p of parts) v = v == null ? undefined : v[p];
    if (v != null && v !== "") return v;
  }
  return undefined;
};

// GET /articles : seul le filtre proId est supporté (inclut les brouillons
// du pro). Écriture admin via les routes du pro auteur
// (PUT/DELETE /pro/{professionalId}/articles/{id}, POST /pro/{id}/articles) :
// les routes /articles/{id} filtrent sur l'utilisateur courant → 404 admin.
const filters = [
  <ProReferenceInput key="proId" source="proId" label="Pro auteur" alwaysOn />,
];

const Form = () => (
  <SimpleForm>
    <TextInput source="title" label="Titre" validate={required()} fullWidth />
    <ProReferenceInput source="professionalId" label="Auteur (professionnel)" validate={required()} />
    <TextInput source="themeId" label="Thème" />
    <TextInput source="excerpt" label="Chapô" multiline fullWidth />
    <TextInput source="content" multiline minRows={10} fullWidth label="Contenu" validate={required()} />
    <TextInput source="imageUrl" label="URL de l'image" fullWidth />
    <TextInput source="link" label="Lien" fullWidth />
    <BooleanInput source="published" label="Publié" defaultValue={false} />
    <BooleanInput source="topPriority" label="Prioritaire" />
  </SimpleForm>
);

const Publish = () => {
  const r = useRecordContext();
  if (!r?.professionalId) return null;
  return (
    <ConfirmActionButton
      label={r.published ? "Dépublier" : "Publier"}
      color={r.published ? "warning" : "success"}
      confirmTitle={r.published ? "Dépublier l'article" : "Publier l'article"}
      confirmText={`${r.published ? "Dépublier" : "Publier"} « ${r.title || r.id} » ?`}
      successMessage="Statut mis à jour"
      onConfirm={() =>
        httpClient(`${API_BASE_URL}/pro/${encodeURIComponent(r.professionalId)}/articles/${encodeURIComponent(r.id)}`, {
          method: "PUT",
          body: JSON.stringify({ ...r, published: !r.published }),
        })
      }
    />
  );
};

export default {
  list: () => (
    <List filters={filters}>
      <Datagrid rowClick="show">
        <TextField source="title" label="Titre" />
        <TextField source="authorDisplayName" label="Auteur" />
        <UserRefField source="professionalId" label="Professionnel" />
        <BooleanField source="published" label="Publié" />
        <FrDateField source="createdAt" label="Créé le" showTime />
      </Datagrid>
    </List>
  ),
  show: () => (
    <Show>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Article">
          <TextField source="title" label="Titre" />
          <TextField source="authorDisplayName" label="Auteur" />
          <UserRefField source="professionalId" label="Professionnel" />
          <BooleanField source="published" label="Publié" />
          <FrDateField source="createdAt" showTime />
          <TextField source="content" />
          <Publish />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Commentaires">
          <RemoteList
            buildPath={(r) => `/articles/${r.id}/comments?limit=100`}
            extract={(json) => json?.items || json?.content || (Array.isArray(json) ? json : [])}
            emptyText="Aucun commentaire"
            columns={[
              { key: "content", label: "Commentaire", render: (c) => pick(c, "content", "text") || "—" },
              { key: "author", label: "Auteur", render: (c) => pick(c, "authorDisplayName", "authorName", "authorId") || "—" },
              { key: "date", label: "Date", render: (c) => fmtDate(pick(c, "createdAt", "date")) },
            ]}
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Brut">
          <JsonField source="__all__" />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  ),
  edit: () => <Edit mutationMode="pessimistic"><Form /></Edit>,
  create: () => <Create><Form /></Create>,
};
